<template>
    <div>
        <Cms
            model="promo-bar"
            v-show="false"
            is-data
            driver="builder"
            v-model="promoText"
            :searchParams="searchParams"
            id="cms_component"
        />
        <section
            v-if="promos.length"
            class="desktop-accordion"
            style="overflow: hidden"
        >
            <details
                class="accordion-details"
                id="desktop_promo_bar_accordion_details"
                ref="accordionDetails"
            >
                <summary class="accordion-summary" role="button" tabindex="0">
                    <div class="accordion-summary__content">
                        <div
                            class="accordion-summary__content-text"
                            :class="{
                                'animate-slide-up': isAnimating,
                                'animate-second-slide-up': isSecondAnimating
                            }"
                            @animationend="animate"
                        >
                            <span
                                class="accordion-summary__content-text title-xs"
                            >
                                {{ promos[currentBannerIndex].title }}
                            </span>
                            <a
                                v-if="(promos[currentBannerIndex].cta)"
                                v-bind="
                                    getCtaLink(promos[currentBannerIndex].cta)
                                "
                                class="accordion-summary__content-link"
                                aria-label="Shop New Launch"
                            >
                                {{
                                    promos[currentBannerIndex].cta.label
                                }}
                            </a>
                        </div>
                        <button
                            class="accordion-arrow accordion-arrow-desktop"
                            aria-label="promo dropdown"
                        >
                            <DoubleChevron
                                alt="Arrow down icon"
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </summary>
                <div class="accordion-content">
                    <div
                        class="accordion-text"
                        v-for="(textObj, index) in promos"
                        :key="index"
                    >
                        <p class="title-s accordion-text__title">
                            {{ textObj.title }}
                        </p>
                        <p class="paragraph-details accordion-text__subtitle">
                            {{ textObj.promoBody }}
                        </p>
                        <rothco-cta :ctaType="textObj.cta.type == 'link' ? 'a' : textObj.cta.type" v-bind="getCtaLink(textObj.cta)" :class="textObj.cta.style" :aria-label="textObj.cta.ariaLabel" :onSuccess="closePromoBar">
                            {{ textObj.cta.label }}
                        </rothco-cta>
                        <hr v-if="index != promos.length -1" />
                    </div>
                </div>
            </details>
        </section>
        <section
            v-if="promos.length"
            class="mobile-accordion"
            style="overflow: hidden"
        >
            <details class="accordion-details accordion-details-mobile">
                <summary class="accordion-summary" role="button" tabindex="0">
                    <div class="accordion-summary__content-mobile">
                        <div
                            class="accordion-summary__content-text-mobile"
                            :class="{
                                'animate-slide-up': isAnimating,
                                'animate-second-slide-up': isSecondAnimating
                            }"
                            @animationend="animate"
                        >
                            <span
                                class="accordion-summary__content-text title-xs"
                            >
                                {{ promos[currentBannerIndex].title }}
                            </span>
                    
                            <a
                                v-if=" (promos[currentBannerIndex].cta)"
                                v-bind="
                                    getCtaLink(promos[currentBannerIndex].cta)
                                "
                                class="accordion-summary__content-link"
                            >
                                {{
                                    promos[currentBannerIndex].cta.label
                                }}
                            </a>
                        </div>
                        <button
                            class="accordion-arrow"
                            aria-label="promo dropdown"
                        >
                            <DoubleChevron
                                aria-hidden="true"
                                alt="Arrow down icon"
                            />
                        </button>
                    </div>
                </summary>
                <div class="accordion-content accordion-content-mobile">
                    <div
                        class="accordion-text"
                        v-for="(textObj, index) in promos"
                        :key="index"
                    >
                        <p class="title-s accordion-text__title">
                            {{ textObj.title }}
                        </p>
                        <p class="paragraph-details accordion-text__subtitle">
                            {{ textObj.promoBody }}
                        </p>
                        <rothco-cta v-if="textObj.cta" :ctaType="textObj.cta.type == 'link' ? 'a' : textObj.cta.type" v-bind="getCtaLink(textObj.cta)" :class="textObj.cta.style" :aria-label="textObj.cta.ariaLabel" :onSuccess="closePromoBar">
                            {{ textObj.cta.label }}
                        </rothco-cta>
                        <hr v-if="index != promos.length -1" />
                    </div>
                </div>
            </details>
        </section>
    </div>
</template>
<script setup lang="ts">
    import {
        ref,
        computed,
        onMounted,
        onBeforeUnmount,
    } from "vue";
    import type { Cta } from "@interfaces/Cta";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    import RothcoCta from "@/components/RothcoCta.vue";
    import Cms from "@/components/Cms.vue";
    import DoubleChevron from "@icons/double-chevron.svg";
    // import { route } from "ziggy-js";

    // const open = ref(false);
    const currentBannerIndex = ref(0);
    const isAnimating = ref(false);
    const isSecondAnimating = ref(false);

    interface Promo {
        title: string;
        promoBody: string;
        cta?: Cta;
    }
    const promoText = ref<Promo[]>([]);
    const props = defineProps({
        promoIntervalSeconds: {
            type: Number,
            default: 5
        },
        fixedPromoIndex: {
            type: Number,
            default: null
        },
        is_dealer: {
            type: Boolean,
            default: false
        }
    });
    const animate = (event: AnimationEvent) => {
        if (event.animationName === "slideUp") {
            isAnimating.value = false;
            let nextBannerIndex = currentBannerIndex.value + 1;
            if (nextBannerIndex > promoText.value.length - 1) {
                nextBannerIndex = 0;
            }
            currentBannerIndex.value = nextBannerIndex;
            isSecondAnimating.value = true;
        } else if (event.animationName === "slideDown") {
            isSecondAnimating.value = false;
        }
    };

    onMounted(() => {
        if (props.fixedPromoIndex != null) {
            currentBannerIndex.value = props.fixedPromoIndex;
            return;
        }
        const interval = setInterval(() => {
            isAnimating.value = true;
        }, props.promoIntervalSeconds * 1000);
        onBeforeUnmount(() => {
            clearInterval(interval);
        });
    });

    const searchParams = computed(() => {
        return {
            "data.dealerPromotion.$eq": props.is_dealer
        };
    });

    const promos = computed(()=>{
        return promoText.value;
    });

    let accordionDetails = ref(null);
    const closePromoBar = () => {
        if (accordionDetails.value) {
            accordionDetails.value.removeAttribute("open");
        }
    };
</script>

<style lang="scss">
    .animate-slide-up {
        animation: slideUp 0.5s forwards;
    }

    .animate-second-slide-up {
        animation: slideDown 0.5s forwards;
    }

    @keyframes slideUp {
        from {
            transform: translateY(0);
        }
        to {
            transform: translateY(-150%);
        }
    }

    @keyframes slideDown {
        from {
            transform: translateY(150%);
        }
        to {
            transform: translateY(0);
        }
    }
</style>
